import {
  currentProviderId,
} from '@/core/composables/useSessionData'
import { useAxios } from '@/core/composables/useAxios'
import { useErrorsBus } from '@/core/composables/useErrorsBus'

import getParamsFromUrl from '~/core/utils/getParamsFromUrl'
import arrayRange from '~/core/utils/arrayRange'

const axios = useAxios()
const requestCache = new Map()
const { errorsBus } = useErrorsBus()

function getExpand(expand = []) {
  return expand.length > 0 ? { expand: expand.join(',') } : undefined
}

function getQueryString(queryParameters = {}) {
  const params = Object.entries(queryParameters)
    .map(([a, b]) => [a.toLowerCase(), b])
    .sort((a, b) => a[0].localeCompare(b[0]))
    .reduce((params, param) => {
      if (Array.isArray(param[1]))
        params.append(param[0], param[1].join(','))
      else params.append(param[0], param[1])

      return params
    }, new URLSearchParams())
    .toString()
  return params ? `?${params}` : ''
}

async function getAllRemainingPagesInParallel({
  initialApiGetFunction,
  resultsKey,
  abortController,
}) {
  let results = []
  const data = await initialApiGetFunction()
  const { count, next } = data
  const dataResultsKey = resultsKey || data.results ? 'results' : 'rows'
  if (!next)
    return data[dataResultsKey]
  const { origin, pathname, search } = new URL(next)
  const endpoint = origin + pathname
  const params = Object.fromEntries(new URLSearchParams(search))
  const pageSize = +params.page_size || results.length
  if (pageSize > 1)
    results = results.concat(data[dataResultsKey])
  const nextPage = +params.page
  const countPrev = pageSize * (nextPage - 1)
  const countRemaining = count - countPrev
  const pagesRemaining = Math.ceil(countRemaining / pageSize)
  const lastPage = nextPage + pagesRemaining - 1

  try {
    const requestObjects = arrayRange(nextPage, lastPage).map(page => ({
      url: endpoint,
      queryParameters: {
        ...params,
        page,
        page_size: pageSize,
      },
      abortController,
      noInflightCache: true,
    }))
    const promises = requestObjects.map(requestObj => request(requestObj))
    const allResponses = await Promise.all(promises)
    results = results.concat(
      allResponses.flatMap(response => response.data[dataResultsKey]),
    )
    return results
  }
  catch (e) {
    return Promise.reject(e)
  }
}

function inferPagingFromApiResponse(response) {
  const count = response.count || response.total
  const prev = response.previous
  const next = response.next

  let perPage
  let currentPage

  const cursor = prev || next
  if (cursor)
    perPage = +getParamsFromUrl(cursor).page_size
  // else perPage = count

  if (!prev) {
    currentPage = 1
  }
  else {
    const page = getParamsFromUrl(prev).page
    currentPage = !page ? 2 : +page + 1
  }

  return {
    count,
    total: count,
    page: currentPage,
    currentPage,
    perPage,
    pageSize: perPage,
  }
}

// request
// api service request method supporting request caching and some other options
// @param {string} method - one of 'GET', 'POST', 'PUT', 'DELETE'
// @param {string} url - the endpoint URL
// @param {object} data - the data to include in the JSON request body
// @param {object} headers - any custom headers to set in the request
// @param {object} queryParameters - any custom query string parameters to include in the endpoint url
// @param {array} expand - keys of any fields to expand in the response
// @param {boolean} allow404 - if true, will not emit 'http-error' on 404 response
// @param {boolean} noInflightCache - disable inflight caching
// @param {number} cacheMs - number of milliseconds to cache request
// @param {AbortController} abortController - abort controller to allow request cancellation
// @return {Promise} response
// @emits http-error
// more info: https://axios-http.com/docs/req_config
function request({
  method = 'GET',
  url,
  data = {},
  headers = {},
  queryParameters = {},
  expand = [],
  allow404 = false,
  noInflightCache = false,
  cacheMs = 0,
  abortController,
}) {
  return new Promise((resolve, reject) => {
    const params = { ...queryParameters, ...getExpand(expand) }
    const qs = getQueryString(params)
    if (qs)
      url += qs

    // return status >= 200 && status < 300; // default
    const validateStatus = allow404
      ? {
          validateStatus(status) {
            return (status >= 200 && status < 300) || status === 404
          },
        }
      : undefined

    if (method !== 'GET') {
      return resolve(
        sendRequest({
          method,
          url,
          data,
          headers,
          validateStatus,
          abortController,
        }),
      )
    }

    if (!noInflightCache) {
      const cachedPromise = requestCache.get(url)
      if (cachedPromise)
        return resolve(cachedPromise)
    }

    const requestPromise = sendRequest({
      method,
      url,
      data,
      headers,
      validateStatus,
      abortController,
    })
    if (!noInflightCache)
      requestCache.set(url, requestPromise)
    requestPromise
      .then((response) => {
        setTimeout(() => requestCache.delete(url), cacheMs)
        resolve(response)
      })
      .catch((e) => {
        if (!noInflightCache)
          requestCache.delete(url)
        reject(e)
      })
  })
}

// sendRequest
// the lowest-level api service request method.  sends a request with axios, based on axios config
// @param {string} method - one of 'GET', 'POST', 'PUT', 'DELETE'
// @param {string} url - the endpoint URL
// @param {object} data - the data to include in the JSON request body
// @param {object} headers - any custom headers to set in the request
// @param {function} validateStatus - a custom status validator
// @param {AbordController} abortController - abort controller to allow request cancellation
// @return {Promise} response
// @emits http-error
async function sendRequest({
  method = 'GET',
  url,
  data = {},
  headers = {},
  validateStatus = undefined,
  abortController,
}) {
  if (!currentProviderId) {
    console.warn('API SERVICE :: sendRequest - No provider selected!')
    return Promise.reject(new Error('No provider selected'))
  }
  // if (!currentCustomerId) {
  //   console.warn('API SERVICE :: sendRequest - No customer selected!')
  //   return Promise.reject(new Error('No customer selected'))
  // }
  const abortException = () => new DOMException('Aborted', 'AbortError')
  const isAxiosCancellationError = e => e.message === 'canceled'

  try {
    return await axios.request({
      method,
      url,
      data,
      headers: {
        'Teamup-Provider-ID': currentProviderId,
        // 'Teamup-Customer-ID': currentCustomerId,
        ...headers,
      },
      ...validateStatus,
      ...(abortController?.signal && { signal: abortController.signal }),
    })
  }
  catch (err) {
    if (abortController?.signal?.aborted && isAxiosCancellationError(err))
      return Promise.reject(abortException())

    return Promise.reject(err)
  }
}

export {
  getExpand,
  getQueryString,
  request,
  inferPagingFromApiResponse,
  getAllRemainingPagesInParallel,
}
